if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      quantityInputSticky = undefined;
      quantityFormSticky = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input-primary');
        this.quantityInputSticky = this.querySelector('.quantity__input-sticky');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initSwatchPreload();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput && !this.quantityInputSticky) return;

        this.quantityForm = this.querySelector('.product-form__quantity-primary');
        this.quantityFormSticky = this.querySelector('.product-form__quantity-sticky');
        if (!this.quantityForm && !this.quantityFormSticky) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initSwatchPreload() {
        const run = () => {
          ['VariantSwatchMap', 'LongSwatchMap', 'ColorPatternMap'].forEach(mapName => {
            const attrMap = {
              VariantSwatchMap: 'variant-swatch-map',
              LongSwatchMap:    'long-swatch-map',
              ColorPatternMap:  'color-pattern-map',
            };
            const attr = attrMap[mapName];
            const el =
              this.querySelector(`#${mapName}-${this.sectionId}[data-${attr}]`) ||
              this.querySelector(`#${mapName}-${this.dataset.section}[data-${attr}]`);
            if (!el) return;
            let map;
            try { map = JSON.parse(el.textContent || '{}'); } catch { return; }
            Object.values(map).forEach(url => { if (url) this._prefetchImage(url); });
          });
        };

        if ('requestIdleCallback' in window) {
          requestIdleCallback(run, { timeout: 2000 });
        } else {
          setTimeout(run, 300);
        }
      }

      _prefetchImage(url) {
        if (!url || this._prefetched?.has(url)) return;
        if (!this._prefetched) this._prefetched = new Set();
        this._prefetched.add(url);
        const img = new Image();
        img.src = url;
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        this.applyOptimisticImageSwap(target, selectedOptionValues);

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = (this.dataset.url !== productUrl) || this.querySelector('li[data-gang-option]') !== null;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects[data-primary]');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
        const variantSelectsAlt = html.querySelector('variant-selects[data-sticky]');
        if (variantSelectsAlt) {
          HTMLUpdateUtility.viewTransition(this.variantSelectorsAlt, variantSelectsAlt, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          this.applySwatchImageSwap(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Price-Alt');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateCustomLiquid(html);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            theme.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(new CustomEvent('variant:change', {
            detail: {
              sectionId: this.sectionId,
              variant,
              html,
            },
          }));
        };
      }

      updateCustomLiquid(html) {
        this.querySelectorAll('.custom__liquid').forEach(block => {
          if (block.id) {
            const liquid = html.querySelector(`#${block.id}`);
            if (liquid) block.innerHTML = liquid.innerHTML;
          }
        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}--alt`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable);
        const selectors = ['price', 'Price-Alt', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul') || this.querySelector('product-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`) || html.querySelector(`product-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);
            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        const mediaGallery = this.querySelector('media-gallery');
        if (mediaGallery != null) {
          mediaGallery.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
        } else {
          this.querySelector('[id^="Slider-"]')?.scrollTo({ left: 0 });
        }

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const input = this.quantityInput || this.quantityInputSticky;
        const data = {
          cartQuantity: input?.dataset.cartQuantity ? parseInt(input.dataset.cartQuantity) : 0,
          min: input?.dataset.min ? parseInt(input.dataset.min) : 1,
          max: input?.dataset.max ? parseInt(input.dataset.max) : null,
          step: input?.step ? parseInt(input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        if (this.quantityInput) this.quantityInput.min = min;
        if (this.quantityInputSticky) this.quantityInputSticky.min = min;

        if (max) {
          if (this.quantityInput) this.quantityInput.max = max;
          if (this.quantityInputSticky) this.quantityInputSticky.max = max;
        } else {
          this.quantityInput?.removeAttribute('max');
          this.quantityInputSticky?.removeAttribute('max');
        }
        if (this.quantityInput) this.quantityInput.value = min;
        if (this.quantityInputSticky) this.quantityInputSticky.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput && !this.quantityInputSticky) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`) || html.getElementById(`Quantity-Form-${sectionId}-sticky`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm?.querySelector(selector);
          const currentSticky = this.quantityFormSticky?.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!updated) continue;
          if (!current && !currentSticky) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current?.setAttribute(attribute, valueUpdated);
                currentSticky?.setAttribute(attribute, valueUpdated);
              } else {
                current?.removeAttribute(attribute);
                currentSticky?.removeAttribute(attribute);
              }
            }
          } else {
            if (current) current.innerHTML = updated.innerHTML;
            if (currentSticky) currentSticky.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() { return this.querySelector(`product-form`); }
      get productModal() { return document.querySelector(`#ProductModal-${this.dataset.section}`); }
      get pickupAvailability() { return this.querySelector(`pickup-availability`); }
      get variantSelectors() { return this.querySelector('variant-selects[data-primary]'); }
      get variantSelectorsAlt() { return this.querySelector('variant-selects[data-sticky]'); }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'related-products');
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'quick_order_list');
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      // ─── Color/swatch image swap ─────────────────────────────────────────
      applySwatchImageSwap(variant) {
        if (this.dataset.swatchSwap === 'off') return; // swap gate: extensions keep their own media
        const variantUrl = this._lookupMap('VariantSwatchMap', String(variant?.id || ''));
        if (variantUrl) { this._showColorSlide(variantUrl); return; }

        const pos = Number(this.dataset.colorOptionPosition || 0);
        let raw = pos > 0 ? (variant?.[`option${pos}`] || '') : '';
        if (!raw && Array.isArray(variant?.options) && pos > 0) raw = variant.options[pos - 1] || '';
        const colorKey = this._normalize(raw);
        if (!colorKey) { this._removeColorSlide(); return; }

        const longUrl = this._lookupMap('LongSwatchMap', colorKey);
        if (longUrl) { this._showColorSlide(longUrl); return; }

        const patternUrl = this._lookupMap('ColorPatternMap', colorKey);
        if (patternUrl) { this._showColorSlide(patternUrl); return; }

        this._removeColorSlide();
      }

      applyOptimisticImageSwap(target, selectedOptionValues) {
        if (this.dataset.swatchSwap === 'off') return; // swap gate: extensions keep their own media
        const variantId = this._resolveVariantIdFromDOM(target);
        if (variantId) {
          const variantUrl = this._lookupMap('VariantSwatchMap', variantId);
          if (variantUrl) { this._showColorSlide(variantUrl); return; }
        }

        const changedSel = target.tagName === 'SELECT'
          ? target
          : target.closest('select')
          || this.querySelector('select[data-hair-color-native-select]');
        const raw = changedSel ? (changedSel.value || target.value || '') : (target.value || '');
        const colorKey = this._normalize(raw);
        if (!colorKey) return;

        const longUrl = this._lookupMap('LongSwatchMap', colorKey);
        if (longUrl) { this._showColorSlide(longUrl); return; }

        const patternUrl = this._lookupMap('ColorPatternMap', colorKey);
        if (patternUrl) { this._showColorSlide(patternUrl); return; }
      }

      // ─── HL CUSTOM: colour preview slide ─────────────────────────────────
      // The swatch image used to overwrite the src of whichever gallery image
      // was active, so the model photo (or any photo the shopper was looking
      // at) was gone until reload. The colour image now lives in its own slide
      // and thumbnail at the end of the gallery; the product's real media is
      // never touched. See dev docs → Colour preview slide.
      _colorSlideId() { return `${this.dataset.section}-hl-color`; }

      _mediaGallery() { return this.querySelector('media-gallery'); }

      _colorSlide() {
        return this.querySelector(`media-gallery li[data-media-id="${this._colorSlideId()}"]`);
      }

      _swapImg(img, url) {
        if (!img) return;
        img.setAttribute('src', url);
        img.setAttribute('data-original-src', url);
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-original-srcset');
        img.removeAttribute('loading');
      }

      _showColorSlide(url) {
        if (!url) return;
        const gallery = this._mediaGallery();
        const list = gallery?.querySelector('ul.product__media-list') || gallery?.querySelector('ul');
        if (!list) return;
        const id = this._colorSlideId();
        let slide = this._colorSlide();

        if (!slide) {
          const source = list.querySelector('li[data-media-id]:not([data-media-id$="-hl-color"])');
          if (!source) return;
          slide = source.cloneNode(true);
          slide.id = `Slide-${id}`;
          slide.dataset.mediaId = id;
          slide.dataset.mediaPosition = String(list.querySelectorAll('li[data-media-id]').length + 1);
          slide.classList.remove('is-active', 'product__media-item--variant', 'product__media-item--full', 'gang__active');
          slide.classList.add('product__media-item--hl-color');
          slide.removeAttribute('data-gang-option');
          slide.removeAttribute('data-gang-connect');
          slide.removeAttribute('data-variant-id');
          slide.querySelectorAll('noscript').forEach((n) => n.remove());
          // No zoom modal for the swatch photo: it has no entry in the product modal.
          slide.querySelectorAll('modal-opener').forEach((opener) => {
            const wrap = document.createElement('div');
            wrap.className = 'product__media-item-hl-color-wrap';
            while (opener.firstChild) wrap.appendChild(opener.firstChild);
            opener.replaceWith(wrap);
          });
          list.append(slide);

          const thumbs = gallery.querySelector('.thumbnail-list');
          const sourceThumb = thumbs?.querySelector('li[data-target]:not([data-target$="-hl-color"])');
          if (thumbs && sourceThumb) {
            const thumb = sourceThumb.cloneNode(true);
            thumb.id = `Slide-Thumbnails-${id}`;
            thumb.dataset.target = id;
            thumb.dataset.mediaPosition = slide.dataset.mediaPosition;
            thumb.classList.remove('is-active', 'thumbnail-list_item--variant', 'gang__active');
            thumb.classList.add('thumbnail-list__item--hl-color');
            thumb.removeAttribute('data-gang-option');
            thumb.removeAttribute('data-gang-connect');
            const button = thumb.querySelector('button');
            if (button) {
              button.removeAttribute('aria-current');
              button.setAttribute('aria-label', 'Selected color');
              button.removeAttribute('aria-describedby');
              button.addEventListener('click', () => gallery.setActiveMedia?.(id, false, true));
            }
            const timg = thumb.querySelector('img');
            if (timg) { timg.id = `Thumbnail-${id}`; timg.setAttribute('alt', 'Selected color'); }
            thumbs.append(thumb);
            const count = parseInt(thumbs.dataset.mediaCount || '0', 10);
            if (count) thumbs.dataset.mediaCount = String(count + 1);
          }
        }

        const img = slide.querySelector('img');
        if (img && img.getAttribute('src') !== url) this._swapImg(img, url);
        const thumbImg = gallery.querySelector(`.thumbnail-list li[data-target="${id}"] img`);
        if (thumbImg && thumbImg.getAttribute('src') !== url) this._swapImg(thumbImg, url);

        gallery.setActiveMedia?.(id, false, false);
      }

      _removeColorSlide() {
        const gallery = this._mediaGallery();
        const slide = this._colorSlide();
        if (!gallery || !slide) return;
        const wasActive = slide.classList.contains('is-active');
        const id = this._colorSlideId();
        slide.remove();
        const thumbs = gallery.querySelector('.thumbnail-list');
        const thumb = thumbs?.querySelector(`li[data-target="${id}"]`);
        if (thumb) {
          thumb.remove();
          const count = parseInt(thumbs.dataset.mediaCount || '0', 10);
          if (count > 1) thumbs.dataset.mediaCount = String(count - 1);
        }
        if (wasActive) {
          const first = gallery.querySelector('ul li[data-media-id]');
          if (first) gallery.setActiveMedia?.(first.dataset.mediaId, false, false);
        }
      }

      // ─── Private helpers ──────────────────────────────────────────────────

      _resolveVariantIdFromDOM(target) {
        const vs = this.querySelector('variant-selects[data-primary]');
        if (!vs) return null;
        const selects = Array.from(vs.querySelectorAll('select[name^="options["]'));
        if (!selects.length) return null;
        const changedSel = target.tagName === 'SELECT'
          ? target
          : target.closest('select') || target.parentElement;
        const currentValues = selects.map(sel => {
          const isChanged = sel === changedSel || (changedSel && sel.name === changedSel.name);
          return isChanged ? (target.value || sel.value) : sel.value;
        });
        const variantsEl = vs.querySelector('script[data-variants-json]');
        if (!variantsEl) return null;
        let variants;
        try { variants = JSON.parse(variantsEl.textContent || '[]'); } catch { return null; }
        const match = variants.find(v =>
          v?.options?.length === currentValues.length &&
          v.options.every((opt, i) => String(opt).trim() === (currentValues[i] || '').trim())
        );
        return match?.id ? String(match.id) : null;
      }

      _lookupMap(mapName, key) {
        if (!key) return null;
        const attrMap = {
          VariantSwatchMap: 'variant-swatch-map',
          LongSwatchMap:    'long-swatch-map',
          ColorPatternMap:  'color-pattern-map',
        };
        const attr = attrMap[mapName] || mapName.toLowerCase();
        const el =
          this.querySelector(`#${mapName}-${this.sectionId}[data-${attr}]`) ||
          this.querySelector(`#${mapName}-${this.dataset.section}[data-${attr}]`);
        if (!el) return null;
        let map;
        try { map = JSON.parse(el.textContent || '{}'); } catch { return null; }

        // ✅ FIX: solo match exacto — sin fallback por substring
        return map[key] || null;
      }

      _normalize(s) {
        return String(s || '').trim().toLowerCase().replace(/\s+/g, '').replace(/[-_]/g, '');
      }

      _saveOriginalSrc(img) {
        if (!img.dataset.preForceSrc)    img.dataset.preForceSrc    = img.getAttribute('src')    || '';
        if (!img.dataset.preForceSrcset) img.dataset.preForceSrcset = img.getAttribute('srcset') || '';
        if (!img.dataset.preForceSizes)  img.dataset.preForceSizes  = img.getAttribute('sizes')  || '';
      }

      _forceImg(img, url) {
        this._saveOriginalSrc(img);
        img.setAttribute('src', url);
        img.setAttribute('data-original-src', url);
        img.setAttribute('data-original-srcset', url);
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.removeAttribute('data-src');
        img.removeAttribute('data-srcset');
        img.dataset.forcedSwatch = '1';
      }

      _restoreImg(img) {
        if (!img?.dataset.forcedSwatch) return;
        if (img.dataset.preForceSrc) {
          img.setAttribute('src', img.dataset.preForceSrc);
          img.setAttribute('data-original-src', img.dataset.preForceSrc);
        }
        if (img.dataset.preForceSrcset) {
          img.setAttribute('srcset', img.dataset.preForceSrcset);
          img.setAttribute('data-original-srcset', img.dataset.preForceSrcset);
        } else {
          img.removeAttribute('srcset');
          img.removeAttribute('data-original-srcset');
        }
        if (img.dataset.preForceSizes) {
          img.setAttribute('sizes', img.dataset.preForceSizes);
        } else {
          img.removeAttribute('sizes');
        }
        delete img.dataset.forcedSwatch;
        delete img.dataset.preForceSrc;
        delete img.dataset.preForceSrcset;
        delete img.dataset.preForceSizes;
      }

      _findMainGalleryImg() {
        const selectors = [
          'media-gallery li[aria-current="true"] img',
          'media-gallery li.is-active img',
          'media-gallery [data-media-id] img',
          '.product__media-item.is-active img',
          '.product__media img',
          'media-gallery img',
        ];
        for (const sel of selectors) {
          const img = this.querySelector(sel);
          if (img && img.src && !img.src.includes('no-image')) return img;
        }
        return this.querySelector('media-gallery img') || this.querySelector('.product__media img');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}